export const VOTE_COLUMNS = [
  { type: 'committee_regular', label: 'Comm', title: 'Regular Committee Vote' },
  { type: 'pi_vote', label: 'PI', title: 'Postpone Indefinitely Vote' },
  { type: 'appropriations', label: 'Appr', title: 'Appropriations Committee Vote' },
  { type: 'third_reading', label: '3rd', title: 'Third Reading / Final Floor Vote' },
] as const;

export interface VotesMatrixLegislatorVote {
  billId: number;
  vote: string;
  voteType: string;
  isAligned?: boolean | null;
}

export interface VotesMatrixLegislator {
  legislatorId: number;
  legislatorName: string;
  party: string;
  chamber: string;
  isPriority?: boolean;
  alignmentPercentage: number | null;
  alignedVotes?: number;
  thirdReadingVotes?: number;
  votes?: VotesMatrixLegislatorVote[];
  workspaces?: { id?: string; name: string; position?: string }[];
}

export interface VotesMatrixBill {
  billId: number;
  billNumber: string;
  billTitle: string;
  trackingPosition?: string;
  votingLegislators?: { legislatorId: number; vote: string }[];
  workspacePositions?: { workspace_name?: string; workspace?: string; position: string }[];
}

export function getVoteByType(votes: VotesMatrixLegislatorVote[], voteType: string): string {
  const match = votes.find(v => v.voteType === voteType);
  return match ? match.vote : '-';
}

export function getVoteColorClass(vote: string): string {
  if (vote === 'Y') return 'text-success-600 dark:text-success-400';
  if (vote === 'N') return 'text-error-600 dark:text-error-400';
  if (vote === '-') return 'text-text-tertiary dark:text-text-dark-tertiary';
  return 'text-text-secondary dark:text-text-dark-secondary';
}

export function getAlignmentColor(pct: number): string {
  if (pct >= 80) return 'text-success-600 dark:text-success-400';
  if (pct >= 60) return 'text-warning-600 dark:text-warning-400';
  return 'text-error-600 dark:text-error-400';
}

export function getPositionBadgeClass(position: string): string {
  switch (position) {
    case 'Support':
      return 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300';
    case 'StrSupport':
      return 'bg-success-200 text-success-900 dark:bg-success-800/30 dark:text-success-200';
    case 'Oppose':
      return 'bg-error-100 text-error-800 dark:bg-error-900/20 dark:text-error-300';
    case 'StrOppose':
      return 'bg-error-200 text-error-900 dark:bg-error-800/30 dark:text-error-200';
    default:
      return 'bg-background-tertiary text-text-secondary dark:bg-background-dark-tertiary dark:text-text-dark-secondary';
  }
}

export function filterMatrixData(
  allBills: VotesMatrixBill[],
  allLegs: VotesMatrixLegislator[],
  hideNoVotes: boolean
): { filteredBills: VotesMatrixBill[]; matrixLegs: VotesMatrixLegislator[]; hiddenBills: number; hiddenLegs: number } {
  const legsWithVotes = new Set(allLegs.filter(l => (l.votes || []).length > 0).map(l => l.legislatorId));
  const billsWithVotes = new Set(
    allBills
      .filter(b => (b.votingLegislators || []).length > 0)
      .map(b => b.billId)
  );

  const filteredBills = (hideNoVotes ? allBills.filter(b => billsWithVotes.has(b.billId)) : allBills)
    .slice()
    .sort((a, b) => (a.billNumber || '').localeCompare(b.billNumber || '', undefined, { numeric: true }));

  const matrixLegs = hideNoVotes ? allLegs.filter(l => legsWithVotes.has(l.legislatorId)) : allLegs;

  return {
    filteredBills,
    matrixLegs,
    hiddenBills: allBills.length - filteredBills.length,
    hiddenLegs: allLegs.length - matrixLegs.length,
  };
}

type VoteTotalKey = 'Y' | 'N' | 'E';

/** Count votes per vote-type column for a legislator's votes on the given bill set. */
export function countVotesByColumn(
  votes: VotesMatrixLegislatorVote[],
  billIds: Set<number>,
  voteVal: VoteTotalKey
): number[] {
  const scoped = votes.filter(v => billIds.has(v.billId));
  return VOTE_COLUMNS.map(col => {
    if (voteVal === 'E') {
      return scoped.filter(v => v.voteType === col.type && (v.vote === 'E' || v.vote === 'V')).length;
    }
    return scoped.filter(v => v.voteType === col.type && v.vote === voteVal).length;
  });
}

export function mapBillWorkspaces(bill: VotesMatrixBill): { name: string; position?: string }[] {
  return (bill.workspacePositions || []).map(wp => ({
    name: wp.workspace_name || wp.workspace || '',
    position: wp.position,
  }));
}

/** Workspace bills may use trackingPosition; legacy public payloads used billPosition. */
export function getBillTrackingPosition(bill: VotesMatrixBill): string {
  return bill.trackingPosition || (bill as { billPosition?: string }).billPosition || '—';
}

export interface VotesReportClientFilterState {
  legislatorParty?: string;
  legislatorChamber?: string;
  voteType?: string;
  priorityOnly?: boolean;
  hasNotes?: boolean;
}

function hasActiveVotesReportClientFilters(filters: VotesReportClientFilterState): boolean {
  return Boolean(
    filters.legislatorParty ||
    filters.legislatorChamber ||
    filters.voteType ||
    filters.priorityOnly ||
    filters.hasNotes
  );
}

/**
 * Client-side Votes Report filtering for org mode (org API returns unfiltered data).
 * Workspace mode should refetch with server-side filters instead.
 */
export function applyVotesReportClientFilters<
  T extends {
    summary: {
      totalTrackedLegislators: number;
      totalTrackedBills: number;
      totalVoteMatches: number;
    };
    legislatorBreakdown: VotesMatrixLegislator[];
    billBreakdown: VotesMatrixBill[];
  }
>(data: T, filters: VotesReportClientFilterState, matchesParty: (raw?: string | null, filter?: string | null) => boolean): T {
  if (!hasActiveVotesReportClientFilters(filters)) return data;

  let legs = data.legislatorBreakdown.slice();

  if (filters.legislatorParty) {
    legs = legs.filter((l) => matchesParty(l.party, filters.legislatorParty));
  }
  if (filters.legislatorChamber) {
    legs = legs.filter((l) => l.chamber === filters.legislatorChamber);
  }
  if (filters.priorityOnly) {
    legs = legs.filter((l) => l.isPriority);
  }
  if (filters.hasNotes) {
    legs = legs.filter((l) => ((l as { relatedNotes?: unknown[] }).relatedNotes || []).length > 0);
  }
  if (filters.voteType) {
    legs = legs
      .map((l) => ({
        ...l,
        votes: (l.votes || []).filter((v) => v.vote === filters.voteType),
      }))
      .filter((l) => (l.votes || []).length > 0);
  }

  const legIds = new Set(legs.map((l) => l.legislatorId));

  const bills = data.billBreakdown
    .map((b) => ({
      ...b,
      votingLegislators: (b.votingLegislators || []).filter((v) => {
        if (!legIds.has(v.legislatorId)) return false;
        if (filters.voteType) return v.vote === filters.voteType;
        return true;
      }),
    }))
    .filter((b) => (b.votingLegislators || []).length > 0);

  const totalVoteMatches = legs.reduce((sum, l) => sum + (l.votes || []).length, 0);

  return {
    ...data,
    summary: {
      ...data.summary,
      totalTrackedLegislators: legs.length,
      totalTrackedBills: bills.length,
      totalVoteMatches,
    },
    legislatorBreakdown: legs,
    billBreakdown: bills,
  };
}
